/**
 * @module app.core
 */

import { autoinject, bindingMode, TaskQueue } from "aurelia-framework";
import { bindable } from 'aurelia-typed-observable-plugin';
import { State, ComponentBase, IForm } from "app/shared/lib/essette-site";
import { ITasksConfig, INotesConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { TaskService } from 'app/core/services/task-service';
import { QueueService } from 'app/core/services/queue-service';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { apiCall } from "app/shared/lib/essette-api";
import { safe, deepClone } from "app/shared/lib/utilities";
import { IDynamicView } from "app/shared/config/modules";
import { Toaster } from 'app/shared/lib/toaster';
import { Indexed } from 'app/shared/lib/types';
import { IPopup } from 'app/shared/elements/popup-base';
import { fireEvent } from 'app/shared/lib/fire-events';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import { validate } from 'app/shared/elements/ess-rule';
import { EssTabsHorizontalCustomElement } from 'app/shared/elements/ess-tabs-horizontal';
import { EssDialogCustomElement } from 'app/shared/elements/ess-dialog';
import { EventAggregator } from "aurelia-event-aggregator";
import { TitleChangeEvent } from "app/shared/lib/events";
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { replaceCopyrightText } from 'app/ext/mcg-informed/lib/utilities';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import * as Iccc from 'app/ext/iccc/interfaces';
import { getReference } from 'app/shared/models/essette-types';

export interface ITaskBinding {
    targetId: string;
    relatedModels?: Indexed<string>;
    taskId: string;
    smartTaskTargetId: string;
    tasksConfig: ITasksConfig;
    notesConfig: INotesConfig;
    queuesConfig: IQueuesConfig;
    readonly: boolean;
    canDeactivateNotes: boolean;
    showAppointments: boolean;
    showTaskQueues: boolean;
    canUpdateTaskDueDate: boolean;
    showDelegateToQueue: boolean;
    showDelegateToUser: boolean;
    canSaveTask: boolean;
    activeTab?: string;
    isDisabled?: boolean;
};

export interface ITaskContainer {
    completeTask(navigateBack?: boolean): Promise<boolean>;
};

export interface ISmartTaskBinding {
    state: State;
    task: Core.Task;
    targetId: string;
    member: CD.Member;
    tasksConfig: ITasksConfig;
    container: ITaskContainer;
    readonly: boolean;
    relatedModels?: Indexed<string>;
    isDisabled: boolean;
};

export interface ITaskFollowupBinding {
    taskDefinitionId: string;
    targetId: string;
    tasksConfig: ITasksConfig;
};

export interface IMemberTask extends Core.Task {
    member?: CD.Member | null;
};

enum tabs {
    details = 'details',
    smartTask = 'smartTask',
    notes = 'notes',
    appointments = 'appointments',
    resources = 'resources'
};

interface ITaskCompleteCustomEvent { task: Core.Task };
export type TaskCompleteCustomEvent = CustomEvent<ITaskCompleteCustomEvent>;

const SMART_TASK_ROUTE: string = '3051';
const SMART_INTERVENTION_ROUTE: string = '3079';

@autoinject()
export class Task extends ComponentBase implements ITaskContainer {

    ////****************** Fields and Properties ************************************////
    protected container: ITaskContainer = this;

    protected validate = validate;
    protected isValid: boolean = false;

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected taskBinding: ITaskBinding | null = null;

    protected targetId!: string;
    protected taskId!: string;
    protected smartTaskTargetId!: string;
    protected readonly: boolean = false;
    protected tasksConfig: ITasksConfig = {};
    protected notesConfig: INotesConfig = {};
    protected labelKey: string = 'core:task.$';
    protected cardTitle: string = this.i18n.tr('cm:carePlanIntervention.$');
    protected relatedModels?: Indexed<string>;

    protected taskQueues: ReadonlyArray<Core.Queue> = [];

    protected task: Core.Task = {};
    protected editTask: Core.Task = {};
    protected hasFollowups: boolean = false;
    protected taskLoaded: boolean = false;

    protected delegateToQueueType: string | null = 'Core.User';
    protected queueUsers: ReadonlyArray<Core.User> | null = null;
    protected queueUsersCache = new Map<string, ReadonlyArray<Core.User>>();
    protected queueProviders: Array<CD.Provider> | null = null;
    protected resources: ReadonlyArray<Core.Resource> | null = null;

    private isSmartTask: boolean = true;
    private navigateBackOnComplete: boolean = true;
    protected hasSmartTaskRoute: boolean = false;

    @bindable
    protected mcgCopyright?: string;
    protected iccCopyrightDisclaimer?: string;
    protected mcgCustomContentDisclaimer?: string;

    protected mcgIntervention: Mcg.IIntervention | undefined = undefined;
    protected mcgVersion: string | null = null;

    protected instructions: Iccc.Instruction[] | null = null;

    ////****************** Element References ***************************************////
    protected $followupForm!: IForm;
    protected $followupModal!: IPopup;
    protected $details!: Element;
    protected $tabs!: EssTabsHorizontalCustomElement;
    protected $detailsTab!: Element;
    protected $smartTaskTab!: Element;
    protected $notesTab!: Element;
    protected $appointmentsTab!: Element;
    protected $resourcesTab!: Element;

    @bindable.string({ defaultBindingMode: bindingMode.twoWay })
    protected taskDisplay: string | null = null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly taskQueue: TaskQueue,
        private readonly service: TaskService,
        private readonly queueService: QueueService,
        private readonly mcgService: McgInformedService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        private readonly events: EventAggregator,
        private readonly context: CasePageContext,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;

        this.taskDisplay = this.task.description || "";
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.reload();

        // do an initial validation because if there aren't any
        // visible elements with rules, isValid will remain false [Matthew J]
        if(this.$details) {
            this.isValid = validate(this.$details);
        }
    }


    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        if(this.tasksConfig.primaryTargetType === 'CM.CaseTask') {
            this.hasSmartTaskRoute = this.accountContext.hasRouteAccess(SMART_TASK_ROUTE);
        }
        else if(this.tasksConfig.primaryTargetType === 'CM.CarePlanIntervention') {
            this.hasSmartTaskRoute = this.accountContext.hasRouteAccess(SMART_INTERVENTION_ROUTE);
        }

    }
    protected async reload(): Promise<void> {
        if(!this.taskBinding) { throw new Error('Missing bindable property [task-binding]'); }
        if(!(this.taskBinding.targetId && this.taskBinding.taskId && this.taskBinding.tasksConfig && this.taskBinding.notesConfig && this.taskBinding.queuesConfig)) {
            throw new Error("Could not configure core task component");
        }

        this.checkPermissions();

        this.targetId = this.taskBinding.targetId;
        this.taskId = this.taskBinding.taskId;
        this.smartTaskTargetId = this.taskBinding.smartTaskTargetId;
        this.readonly = this.taskBinding.readonly;

        this.tasksConfig = this.taskBinding.tasksConfig;
        this.notesConfig = this.taskBinding.notesConfig;  // for binding into composed core/notes/note component
        this.labelKey = this.taskBinding.tasksConfig.labelKey || this.labelKey;
        this.relatedModels = this.taskBinding.relatedModels;

        // Configure services
        this.service.configure(this.tasksConfig);
        this.queueService.configure(this.taskBinding.queuesConfig);

        if(this.taskId) {
            await this.loadTask(this.taskId);
        }

        // load queues after task is loaded to make sure Portal Queues are filtered out of Smart Tasks
        if(this.taskBinding.showTaskQueues && !(this.taskQueues && this.taskQueues.length > 0)) {
            await this.loadTaskQueues();
        }

        // activates whatever tab gets set to the activeTab variable
        switch(this.taskBinding.activeTab) {
            case tabs.details: { this.$tabs.activateTab(this.$detailsTab); break; }
            case tabs.smartTask: { this.$tabs.activateTab(this.$smartTaskTab); break; }
            case tabs.notes: { this.$tabs.activateTab(this.$notesTab); break; }
            case tabs.appointments: { this.$tabs.activateTab(this.$appointmentsTab); break; }
            case tabs.resources: { this.$tabs.activateTab(this.$resourcesTab); break; }
        }
        this.taskDisplay = this.task.description || "";
    }

    private updateDelegateQueue(newQueue: Core.Queue | null, delegateTo: Core.TaskDelegateTargetModel | null = null): void {
        if(!(newQueue && newQueue.id)) {
            // Clear Delegated To if Set
            if(!!this.editTask.delegated) {
                this.editTask.delegated.to = null;
            }
            return;
        }

        if(!delegateTo && !!this.editTask.delegated) {
            this.editTask.delegated.to = null;
        }
        this.delegateToQueueType = safe(() => newQueue.delegated!.type!.code) || null;
        if(this.delegateToQueueType === 'Core.User') {
            this.loadQueueUsers(newQueue.id);
        }
        else if(this.delegateToQueueType === 'CD.Provider') {
            this.loadQueueProviders();
        }
    }

    public navigateBack(): void {
        if(!!this.$followupModal) {
            this.$followupModal.close();
            if(this.navigateBackOnComplete) {
                if(this.tasksConfig.backRouteName) {
                    try { this.router.navigateToRoute(this.tasksConfig.backRouteName); }
                    catch { this.router.navigateBack(); }
                } else {
                    this.router.navigateBack();
                }
            }
        }
    }

    public getTaskTypeView(task: Core.Task | null): IDynamicView | null {
        if(task?.definition?.type?.code) {
            let code = task.definition.type.code;
            if(code === 'Core.Assessment' && task.externalSystem?.code === 'CCI') code = 'Mcg.Assessment';
            if(code === 'Core.Assessment' && task.externalSystem?.code === 'ICCC') code = 'Iccc.Assessment';

            const view = this.env.getDynamicViewByKey('Core.TaskType', code);
            this.isSmartTask = !!view;
            return view;
        }
        return null;
    }

    public getTaskFollowupView(tasksConfig: ITasksConfig | null): IDynamicView | null {
        if(tasksConfig && tasksConfig.primaryTargetType) {
            const { primaryTargetType } = tasksConfig;
            return this.env.getDynamicViewByKey('Core.TaskFollowups', primaryTargetType) || null;
        }
        return null;
    }

    protected getSmartTaskBinding(): ISmartTaskBinding {
        const taskMember = <IMemberTask>this.task;
        const smartTask = {
            targetId: this.smartTaskTargetId,
            task: this.task,
            member: taskMember?.member,
            tasksConfig: this.tasksConfig,
            isDisabled: this.taskBinding?.isDisabled ?? false,
            container: this.container,
            readonly: this.readonly,
            state: this.state,
            relatedModels: this.relatedModels
        } as ISmartTaskBinding
        return smartTask;
    }

    protected getFollowupBinding(): ITaskFollowupBinding | null {
        const definitionId = safe(() => this.task.definition!.id);
        if(!definitionId) { return null; }

        const followupBinding: ITaskFollowupBinding = {
            targetId: this.targetId,
            taskDefinitionId: definitionId,
            tasksConfig: this.tasksConfig
        };
        return followupBinding;
    }

    private constructTaskForSave(): Core.Task | null {
        if(!(this.editTask && this.editTask.id)) {
            return null;
        }

        const newTask: Core.Task = {
            id: this.editTask.id
        };

        newTask.due = { date: this.editTask.due?.date };

        if(!!this.taskBinding?.showDelegateToQueue) {
            const saveQueue = getReference(this.editTask?.delegated?.queue);
            if(!!saveQueue) {
                newTask.delegated = {
                    queue: saveQueue
                };

                if(!!this.taskBinding?.showDelegateToUser) {
                    newTask.delegated.to = getReference(this.editTask?.delegated?.to);
                    if(!!newTask.delegated.to) {
                        const targetType = this.editTask?.delegated?.queue?.delegated?.type || null;
                        if(!!targetType) {
                            newTask.delegated.to.type = {
                                id: targetType.id,
                                code: targetType.code
                            }
                        }
                        else {
                            newTask.delegated.to.type = null;
                        }
                    }
                }
            }
            else {
                // Clear Delegated from task/intervention if queue is empty by passing null queue
                newTask.delegated = {
                    "queue": null
                };
            }
        }

        return newTask;
    }

    private async determineFollowups(): Promise<void> {
        // This section mainly for intervention followups
        // First see if the intervention definition has any followups defined
        const followups: Core.TaskDefinition[] = this.task.definition?.followups || [];
        if(followups.length > 0) {
            // Next get the other tasks already attached to target
            const otherTasks = await this.loadTasksByTarget(this.targetId) || [];
            // If a followup already exists on a goal, we ignore it.
            // hasFollowups = true when there is at least one followup not already on the goal
            this.hasFollowups = followups.some((f) => {
                return otherTasks.findIndex((t) => t.definition && t.definition.id === f.id) === -1;
            });

            if(this.hasFollowups) {
                this.taskQueue.queueTask(() => {
                    this.$followupModal.open();
                });
            }
        }
    }


    ////****************** Functions ************************************************////
    protected setCardTitle(): void {
        const taskType = this.i18n.tr(this.task.externalSystem && this.task.externalSystem.code === 'CCI' && this.task.externalIdentifier ? 'ext:cci.intervention.$long' : this.labelKey);
        this.cardTitle = `${taskType}: ${this.task.description}`;
        if(!!this.tasksConfig?.backRouteName && !!taskType && !!this.task.description) {
            this.events.publish(new TitleChangeEvent([taskType, this.task.description]));
        }
    }

    protected isIntervention(): boolean {
        if((this.task as CM.CarePlanIntervention).goal) return true;
        return false;
    }

    private loadIcccIntervention(): void {
        const intervention: CM.CarePlanIntervention = this.task as CM.CarePlanIntervention;
        const carePlanJson: string | null | undefined = intervention.goal?.assessmentAttachment?.externalCarePlanContent?.content;
        if(!carePlanJson) return;

        const goals: Iccc.Goal[] = JSON.parse(carePlanJson);
        const goal: Iccc.Goal | undefined = goals.find(g => g.uid === intervention.goal?.externalIdentifier);
        if(!goal || goal.problems?.length === 0) return;

        problemsLoop:
        for(const p of goal.problems) {
            interventionsLoop:
            for(const i of p.interventions) {
                if(i.uid !== intervention.externalIdentifier) continue;

                this.instructions = i.instructions;
                return;
            }
        }
    }

    protected openPatientEducation(patientEducationContent: HTMLElement, modal: EssDialogCustomElement, value: string): void {
        patientEducationContent.innerHTML = value;
        modal.open();
    }

    private getIccCopyright(content: string): string | undefined {
        if(!content) return undefined;

        const parser = new DOMParser();
        const review: Document = parser.parseFromString(content, 'text/xml');
        const copyright = review.documentElement?.getAttribute('copyright')?.trim();
        return copyright;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadTask(taskId: string): Promise<void> {
        const includes: string[] = [
            'definition.resources',
            'definition.followups',
            'definition.type',
            'definition.type.argument',
            'delegated.queue',
            'delegated.to',
            'externalSystem',
            'goal.assessmentAttachment.externalContent',
            'member',
            'metadata.completed.byUser',
            'metadata.created.byUser'
        ];

        if(this.tasksConfig.primaryTargetType === 'CM.CarePlanIntervention') {
            includes.push('goal', 'goal.assessmentAttachment', 'goal.assessmentAttachment.externalCarePlanContent');
        }

        if(this.tasksConfig.primaryTargetType === 'CM.CaseTask') {
            includes.push('case', 'case.status');
        }

        try {
            this.state.beginLoad();

            const response = await this.service.loadTask(taskId, includes);
            if(!response.isSuccessful || response.results.length === 0) return;
            this.task = response.results[0];
            this.iccCopyrightDisclaimer = undefined;

            if(this.task.externalSystem?.code?.toLowerCase() == 'cci' && this.isIntervention()) {
                await this.loadMcgIntervention();

                // Set custom content disclaimer based on parsed content
                const intervention: CM.CarePlanIntervention = this.task;
                if(intervention.goal?.assessmentAttachment?.externalContent?.content) {
                    const content = intervention.goal.assessmentAttachment.externalContent.content;
                    const assessment: Mcg.Assessment = JSON.parse(content);
                    this.mcgCustomContentDisclaimer = assessment.customGuidelineDisclaimer;
                }
            }
            if(this.task.externalSystem?.code?.toLowerCase() == 'iccc' && this.isIntervention()) {
                this.loadIcccIntervention();

                const intervention: CM.CarePlanIntervention = this.task;
                const content = intervention.goal?.assessmentAttachment?.externalContent?.content;
                if(content) this.iccCopyrightDisclaimer = this.getIccCopyright(content);
            }

            this.editTask = deepClone(this.task);
            this.resources = this.task.definition?.resources || null;
            this.setCardTitle();

            const caseTask = this.tasksConfig.primaryTargetType === 'CM.CaseTask' ? <CM.CaseTask>this.task : null;
            if(!!this.task.isCompleted || this.taskBinding?.readonly || (caseTask && !caseTask?.case?.status?.flag?.isOpen)) {
                this.readonly = true;
            }

            // Queue and Delegated To drop downs
            if(this.editTask && this.editTask.delegated) {
                this.updateDelegateQueue(this.editTask.delegated.queue || null, this.editTask.delegated.to || null);
            }

            this.taskLoaded = true;

            if(this.mcgCopyright && this.isIntervention()) {
                // Set copyright text based on current intervention's external assessment version
                const intervention = this.editTask as CM.CarePlanIntervention;
                const copyright = this.mcgCopyright;
                const edition = intervention.goal?.externalAssessmentVersion ? intervention.goal.externalAssessmentVersion.split('.')[0] : getApiSetting(this.context.settings, 'mcg.informed.edition.$display')?.toString();
                this.mcgCopyright = replaceCopyrightText(copyright, edition);
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMcgIntervention(): Promise<void> {
        const intervention: CM.CarePlanIntervention = this.task as CM.CarePlanIntervention;
        const hsim = intervention.goal?.externalAssessmentIdentifier;
        const edition = intervention.goal?.externalAssessmentVersion;
        if(!hsim || !edition) return;

        this.mcgVersion = edition.indexOf('.') > -1 ? edition.substring(0, edition.indexOf('.')) : edition;
        const guidelineResponse = await this.mcgService.getGuidelineContent(hsim, edition);
        if(!guidelineResponse.isSuccessful) return;

        const mcgAssessment: Mcg.Assessment = guidelineResponse.result;

        sectionsLoop:
        for(const s of mcgAssessment.sectionsSet) {
            subsectionsLoop:
            for(const ss of s.sectionsSet) {
                problemsLoop:
                for(const p of ss.problemsSet) {
                    const goal = p.goalsSet?.find(g => g.uid === intervention.goal?.externalIdentifier);
                    const goalIntervention: Mcg.IIntervention | undefined = goal?.interventionsSet.find(i => i.uid === intervention.externalIdentifier);
                    if(!goalIntervention) continue;

                    this.mcgIntervention = goalIntervention;
                    break sectionsLoop;
                }
            }
        }
    }

    @apiCall
    private async loadTaskQueues(): Promise<void> {
        try {
            this.state.beginLoad();

            const delegatedTypeCode = this.isSmartTask ? 'Core.User' : null; //!!!
            const response = await this.queueService.loadQueues(delegatedTypeCode, { all: true });
            this.taskQueues = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadQueueProviders(): Promise<void> {
        try {
            this.state.beginLoad();

            const memberId = this.tasksConfig.memberId || '';
            const response = await this.queueService.loadPortalProviders(memberId, { all: true });
            this.queueProviders = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadQueueUsers(queueId: string): Promise<void> {
        if(!!this.queueUsers && this.queueUsersCache.has(queueId)) {
            this.queueUsers = this.queueUsersCache.get(queueId) || [];
        }
        else {
            try {
                this.state.beginLoad();
                const response = await this.queueService.loadQueueUsers(queueId, { all: true });
                this.queueUsers = response.results;
                this.queueUsersCache.set(queueId, this.queueUsers);
            } catch(error) {
                throw error;
            } finally {
                this.state.endLoad();
            }
        }
    }

    @apiCall
    protected async saveTask(): Promise<void> {
        const newTask = this.constructTaskForSave();
        if(!(newTask && newTask.id)) {
            throw new Error('Unable to properly construct the save task');
        }

        try {
            this.state.beginSave();
            const response = await this.service.updateTask(newTask.id, newTask);
            this.toaster.standard.saved(this.labelKey, response.isSuccessful);
            if(response.isSuccessful) {
                this.loadTask(newTask.id);
            }
        } catch(error) {
            this.toaster.standard.saved(this.labelKey, false);
            throw error;
        } finally {
            this.state.endSave();
        }

    }

    @apiCall
    public async completeTask(navigateBack: boolean = true): Promise<boolean> {
        if(!this.task.id) { throw new Error('Invalid Task'); }
        if(this.task.isCompleted) { return false; }

        this.navigateBackOnComplete = navigateBack;

        if(!this.isSmartTask) {
            const type: string = this.i18n.tr(this.tasksConfig.primaryTargetType === 'CM.CarePlanIntervention' ? 'cm:carePlanIntervention.$' : 'core:task.$');
            const message: string = this.i18n.tr('core:local.confirmCompleteTask', { what: type });
            const result = await confirmation.showCustom(message);
            if(result.dialogResponse === confirmation.options.NO) { return false; }
        }

        try {
            this.state.beginSave();
            const response = await this.service.completeTask(this.task.id);
            this.toaster.standard.completed(this.labelKey, response.isSuccessful);
            if(response.isSuccessful) {
                if(this.task.definition?.type?.code === "Core.ContactLog") {
                    this.toaster.standard.saved('core:contactLog.attempt.$long');
                }
                this.loadTask(this.task.id).then(async () => {
                    await this.determineFollowups();
                    if(!this.hasFollowups && this.navigateBackOnComplete) {
                        this.navigateBack();
                    } else {
                        fireEvent(this.$element, 'task-complete', { task: this.task } as ITaskCompleteCustomEvent);
                    }
                });
                return true;
            }
            else {
                return false;
            }
        } catch(error) {
            this.toaster.standard.completed(this.labelKey, false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    public async loadTasksByTarget(targetId: string): Promise<Core.Task[]> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadTasksByTarget(targetId, { all: true }, [], false);
            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
